import { use } from "react";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";

import { getConfig } from "lib/get-config";
import { captureException } from "lib/logger";
import { NavigationContext } from "lib/navigation/use-navigation";
import { usePaths } from "lib/navigation/use-paths";

import { deleteBookmark, getBookmarks, postBookmark } from "./get-bookmarks";
import {
  addLocalBookmark,
  deleteLocalBookmark,
  getLocalBookmarks,
} from "./local";

type BookmarkResult = {
  id: string;
  created_at: string;
  xpointer: string;
  percent: number;
  version: number;
  is_epub: boolean;
  chapter_title: string;
};

export function useBookmarks() {
  const { art, user } = getConfig();
  const queryKey = user ? ["bookmarks", art] : ["local-bookmarks", art];

  const { data: bookmarks } = useSuspenseQuery({
    queryKey,
    queryFn: () => (user ? getBookmarks(art) : getLocalBookmarks(art)),
  });

  return {
    bookmarks: (bookmarks || []) as BookmarkResult[],
    queryKey,
  };
}

export function useMutateBookmarks() {
  const { art, user } = getConfig();
  const { page, totalPages } = use(NavigationContext)!;
  const { bookmarks, queryKey } = useBookmarks();
  const { xpointerToChapter, pageToXpointer } = usePaths();
  const queryClient = useQueryClient();

  async function createBookmark(
    xpointer: string
  ): Promise<[BookmarkResult | null, Error | null]> {
    const newBookmark: BookmarkResult = {
      id: uuid(),
      created_at: new Date().toISOString().split(".")[0],
      xpointer: xpointer,
      percent: Math.round((page / totalPages) * 100),
      version: 1,
      is_epub: false,
      chapter_title: xpointerToChapter(xpointer)?.t || "",
    };

    const previous = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, [...bookmarks, newBookmark]);

    try {
      if (user) {
        await postBookmark(art, newBookmark);
      } else {
        addLocalBookmark(newBookmark, art);
      }
      return [newBookmark, null];
    } catch (error) {
      queryClient.setQueryData(queryKey, previous);
      captureException(error as Error);
      return [null, error as Error];
    }
  }

  async function bookmarkPage(): Promise<
    [BookmarkResult | null, Error | null]
  > {
    const xp = pageToXpointer(page);

    if (!xp) {
      console.warn("No xpointer for page", page);
      return [null, new Error("No xpointer for current page")];
    }
    return createBookmark(xp);
  }

  async function removeBookmark(
    bookmarkId: string
  ): Promise<[string | null, Error | null]> {
    const previous = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(
      queryKey,
      bookmarks.filter(b => b.id !== bookmarkId)
    );

    try {
      if (user) {
        await deleteBookmark(art, bookmarkId);
      } else {
        deleteLocalBookmark(bookmarkId);
      }
      return [bookmarkId, null];
    } catch (error) {
      queryClient.setQueryData(queryKey, previous);
      captureException(error as Error);
      return [null, error as Error];
    }
  }

  return {
    createBookmark,
    bookmarkPage,
    deleteBookmark: removeBookmark,
  };
}
